import { Controller } from "@hotwired/stimulus"
import I18n from "../../i18n"

const t = (key, options) => I18n.t(`erp.sales.js.textile_picker.${key}`, options)

// Single dialog for adding textile lines to an estimate: catalogue products (search then
// colour × size matrix) or garments the customer brings in themselves (free-text line).
// Confirming dispatches one event per tab, consumed by erp--estimate-editor.
// Connects to data-controller="erp--textile-picker"
export default class extends Controller {
  static targets = [
    "tabButton", "panel",
    "searchInput", "searchResults", "matrix", "matrixPlaceholder",
    "supplierFilter", "categoryFilter", "compatibilityDetails", "compatibilitySummary",
    "garmentButton", "colorInput", "commentInput", "sizeInput",
    "title", "hint", "submit"
  ]

  static values = {
    searchUrl: String,  // products search endpoint
    matrixUrl: String,  // base path for /products/:id/variants_matrix
    customerId: Number
  }

  connect() {
    this._debounceTimer = null
    this._garmentType = null
    this._mode = "add"
    this._searchAbortController = null
    this._matrixAbortController = null
    this._onOpen = this._onOpen.bind(this)
    this._onCancel = this._onCancel.bind(this)
    this._onBackdropClickBound = this._onBackdropClick.bind(this)
    this._onClose = () => this.#reset()
    document.addEventListener("textile-picker:open", this._onOpen)
    this.element.addEventListener("click", this._onBackdropClickBound)
    this.element.addEventListener("cancel", this._onCancel)
    this.element.addEventListener("close", this._onClose)
    this.#activateTab("catalogue")
  }

  disconnect() {
    clearTimeout(this._debounceTimer)
    this._searchAbortController?.abort()
    this._matrixAbortController?.abort()
    document.removeEventListener("textile-picker:open", this._onOpen)
    this.element.removeEventListener("click", this._onBackdropClickBound)
    this.element.removeEventListener("cancel", this._onCancel)
    this.element.removeEventListener("close", this._onClose)
  }

  // ── Dialog ───────────────────────────────────────────────────

  _onOpen(event) {
    this.#reset()
    const customerField = document.querySelector("select[name='estimate[customer_id]']")
    if (customerField) this.customerIdValue = parseInt(customerField.value) || 0
    this._mode = event.detail?.mode === "replace" ? "replace" : "add"
    if (this.hasTitleTarget) this.titleTarget.textContent = t(this._mode === "replace" ? "replace_title" : "add_title")
    this.submitTarget.textContent = t(this._mode === "replace" ? "replace" : "add")
    this.element.showModal()
    if (!(this.customerIdValue > 0)) this.matrixPlaceholderTarget.textContent = t("select_customer_hint")
    this.searchInputTarget.focus()
  }

  _onBackdropClick(event) {
    if (event.target === this.element) this.close()
  }

  close() {
    this.dispatch("cancelled")
    this.element.close()
  }

  _onCancel(event) {
    event.preventDefault()
    this.close()
  }

  switchTab(event) {
    this.#activateTab(event.currentTarget.dataset.tab)
  }

  navigateTab(event) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return

    event.preventDefault()
    const current = this.tabButtonTargets.indexOf(event.currentTarget)
    const next = event.key === "Home" ? 0
      : event.key === "End" ? this.tabButtonTargets.length - 1
        : (current + (event.key === "ArrowRight" ? 1 : -1) + this.tabButtonTargets.length) % this.tabButtonTargets.length
    const tab = this.tabButtonTargets[next]
    this.#activateTab(tab.dataset.tab)
    tab.focus()
  }

  // ── Catalogue tab ────────────────────────────────────────────

  search() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this.#performSearch(), 250)
  }

  async selectProduct(event) {
    event.preventDefault()
    clearTimeout(this._debounceTimer)

    this.#hideResults()
    this.searchInputTarget.value = ""
    this.matrixTarget.replaceChildren()
    this.matrixPlaceholderTarget.classList.remove("hidden")
    this.matrixPlaceholderTarget.textContent = t("loading")
    this.submitTarget.disabled = true
    if (this.hasCompatibilitySummaryTarget) this.compatibilitySummaryTarget.replaceChildren()
    if (this.hasCompatibilityDetailsTarget) this.compatibilityDetailsTarget.classList.add("hidden")

    this._matrixAbortController?.abort()
    this._matrixAbortController = new AbortController()
    const request = this._matrixAbortController

    try {
      const url = new URL(`${this.matrixUrlValue}/${event.currentTarget.dataset.productId}/variants_matrix`, window.location.origin)
      if (this.hasCustomerIdValue && this.customerIdValue > 0) url.searchParams.set("customer_id", this.customerIdValue)

      const response = await fetch(url, {
        headers: { "Accept": "application/json" },
        signal: request.signal
      })
      if (request !== this._matrixAbortController) return
      if (!response.ok) {
        console.error("[textile-picker] Matrix fetch failed:", response.status)
        this.#setMatrixError()
        return
      }
      this.#renderMatrix(await response.json())
      this.refreshFooter()
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("[textile-picker] Matrix fetch error:", error)
        this.#setMatrixError()
      }
    }
  }

  // ── Customer-supplied tab ────────────────────────────────────

  selectGarmentType(event) {
    this._garmentType = event.currentTarget.dataset.garmentType
    this.garmentButtonTargets.forEach(button => {
      const selected = button.dataset.garmentType === this._garmentType
      button.setAttribute("aria-pressed", selected ? "true" : "false")
      button.classList.toggle("bg-black", selected)
      button.classList.toggle("text-white", selected)
      button.classList.toggle("text-black", !selected)
      button.classList.toggle("bg-erp-card", !selected)
    })
    this.refreshFooter()
  }

  increment(event) {
    this.#step(event.currentTarget, 1)
  }

  decrement(event) {
    this.#step(event.currentTarget, -1)
  }

  // ── Confirm ──────────────────────────────────────────────────

  confirm() {
    if (this.element.closest("form")?.dataset.saveState === "saving") {
      this.hintTarget.textContent = t("wait_for_save")
      return
    }

    if (this._activeTab === "customer") {
      const sizes = this.#customSizes()
      if (!this._garmentType || sizes.length === 0) return

      this.dispatch("custom-selected", {
        detail: {
          garment_type: this._garmentType,
          garment_label: this.#garmentLabel(),
          color_name: this.colorInputTarget.value.trim(),
          comment: this.commentInputTarget.value.trim(),
          sizes
        }
      })
    } else {
      const colours = this.#catalogueColours()
      if (colours.length === 0 || (this._mode === "replace" && !this.#singleColourway(colours))) return

      this.dispatch("items-selected", { detail: { colours } })
    }

    this.close()
  }

  refreshFooter() {
    const isCustom = this._activeTab === "customer"
    const catalogueColours = isCustom ? [] : this.#catalogueColours()
    const quantity = isCustom
      ? this.#customSizes().reduce((sum, size) => sum + size.quantity, 0)
      : catalogueColours.reduce(
        (sum, colour) => sum + colour.sizes.reduce((s, size) => s + size.quantity, 0), 0
      )

    const missingGarmentType = isCustom && !this._garmentType
    const invalidReplacementSelection = this._mode === "replace" && !isCustom && !this.#singleColourway(catalogueColours)
    const valid = quantity > 0 && !missingGarmentType && !invalidReplacementSelection

    this.submitTarget.disabled = !valid
    if (missingGarmentType) {
      this.hintTarget.textContent = t("select_garment_type")
    } else if (invalidReplacementSelection && catalogueColours.length > 1) {
      this.hintTarget.textContent = t("select_one_colourway")
    } else {
      this.hintTarget.textContent = quantity > 0 ? t("pieces", { count: quantity }) : t("fill_quantities")
    }
  }

  // ── Private ──────────────────────────────────────────────────

  #activateTab(tab) {
    this._activeTab = tab

    this.tabButtonTargets.forEach(button => {
      const active = button.dataset.tab === tab
      button.setAttribute("aria-selected", active ? "true" : "false")
      button.tabIndex = active ? 0 : -1
      button.classList.toggle("border-black", active)
      button.classList.toggle("border-transparent", !active)
      button.classList.toggle("text-black", active)
      button.classList.toggle("text-black/60", !active)
      button.classList.toggle("font-semibold", active)
    })

    this.panelTargets.forEach(panel => {
      const active = panel.dataset.tab === tab
      panel.classList.toggle("hidden", !active)
      panel.toggleAttribute("hidden", !active)
    })
    this.refreshFooter()
  }

  #reset() {
    this._searchAbortController?.abort()
    this._matrixAbortController?.abort()
    this.#activateTab("catalogue")
    this.#hideResults()
    this.searchInputTarget.value = ""
    if (this.hasSupplierFilterTarget) this.supplierFilterTarget.value = ""
    if (this.hasCategoryFilterTarget) this.categoryFilterTarget.value = ""
    this.element.querySelectorAll("details").forEach(details => { details.open = false })
    this.matrixTarget.replaceChildren()
    this.matrixPlaceholderTarget.textContent = t("catalogue_hint")
    this.matrixPlaceholderTarget.classList.remove("hidden")
    if (this.hasCompatibilitySummaryTarget) this.compatibilitySummaryTarget.replaceChildren()
    if (this.hasCompatibilityDetailsTarget) this.compatibilityDetailsTarget.classList.add("hidden")

    this._garmentType = null
    this.garmentButtonTargets.forEach(button => {
      button.setAttribute("aria-pressed", "false")
      button.classList.remove("bg-black", "text-white")
      button.classList.add("bg-erp-card", "text-black")
    })
    this.colorInputTarget.value = ""
    this.commentInputTarget.value = ""
    this.sizeInputTargets.forEach(input => { input.value = "0" })

    this.refreshFooter()
  }

  async #performSearch() {
    const query = this.searchInputTarget.value.trim()
    const hasFilter = (this.hasSupplierFilterTarget && this.supplierFilterTarget.value)
      || (this.hasCategoryFilterTarget && this.categoryFilterTarget.value)
    if (query.length < 2 && !hasFilter) {
      this._searchAbortController?.abort()
      this.#hideResults()
      return
    }

    this._searchAbortController?.abort()
    this.#hideResults()
    this._searchAbortController = new AbortController()
    const request = this._searchAbortController

    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      if (query.length >= 2) url.searchParams.set("q", query)
      if (this.hasSupplierFilterTarget && this.supplierFilterTarget.value) {
        url.searchParams.set("supplier", this.supplierFilterTarget.value)
      }
      if (this.hasCategoryFilterTarget && this.categoryFilterTarget.value) {
        url.searchParams.set("category_id", this.categoryFilterTarget.value)
      }

      const response = await fetch(url, {
        headers: { "Accept": "application/json" },
        signal: request.signal
      })
      if (request !== this._searchAbortController) return
      if (!response.ok) {
        console.error("[textile-picker] Search failed:", response.status)
        this.#renderSearchError()
        return
      }
      this.#renderResults(await response.json())
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("[textile-picker] Network error:", error)
        this.#renderSearchError()
      }
    }
  }

  #renderResults(products) {
    this.searchResultsTarget.replaceChildren()

    if (products.length === 0) {
      const empty = document.createElement("div")
      empty.className = "px-3 py-2 text-sm text-black/50"
      empty.textContent = t("no_results")
      this.searchResultsTarget.appendChild(empty)
      this.searchResultsTarget.classList.remove("hidden")
      return
    }

    products.forEach(product => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "min-h-16 w-full text-left px-3 py-3 text-sm hover:bg-black/5 flex items-center gap-3 border-b border-black/10 last:border-0 cursor-pointer"
      button.dataset.action = "click->erp--textile-picker#selectProduct"
      button.dataset.productId = product.id

      const thumb = document.createElement("div")
      thumb.className = "flex-shrink-0"
      if (product.image_url) {
        const img = document.createElement("img")
        img.addEventListener("error", () => thumb.replaceChildren(this.#placeholder("w-8 h-8", "text-xs")), { once: true })
        img.src = product.image_url
        img.alt = `${product.brand} ${product.name}`
        img.className = "w-8 h-8 object-cover rounded border border-black"
        img.loading = "lazy"
        thumb.appendChild(img)
      } else {
        thumb.appendChild(this.#placeholder("w-8 h-8", "text-xs"))
      }

      const text = document.createElement("div")
      text.className = "flex-1 min-w-0 flex items-center justify-between gap-2"

      const label = document.createElement("span")
      label.className = "truncate font-medium"
      label.textContent = `${product.brand} ${product.name}`

      const reference = document.createElement("span")
      reference.className = "text-xs text-black/50 whitespace-nowrap"
      reference.textContent = product.reference || ""

      text.append(label, reference)
      button.append(thumb, text)
      this.searchResultsTarget.appendChild(button)
    })

    this.searchResultsTarget.classList.remove("hidden")
  }

  #hideResults() {
    this.searchResultsTarget.replaceChildren()
    this.searchResultsTarget.classList.add("hidden")
  }

  #renderSearchError() {
    this.searchResultsTarget.replaceChildren()
    const error = document.createElement("p")
    error.className = "px-3 py-3 text-sm text-red-700"
    error.setAttribute("role", "alert")
    error.textContent = t("load_error")
    this.searchResultsTarget.appendChild(error)
    this.searchResultsTarget.classList.remove("hidden")
  }

  #setMatrixError() {
    this.matrixTarget.replaceChildren()
    this.matrixPlaceholderTarget.setAttribute("role", "alert")
    this.matrixPlaceholderTarget.textContent = t("load_error")
    this.matrixPlaceholderTarget.classList.remove("hidden")
    this.submitTarget.disabled = true
  }

  // Colour rows × size columns, one quantity input per catalogue variant.
  #renderMatrix(matrix) {
    this.matrixTarget.replaceChildren()
    this.matrixPlaceholderTarget.setAttribute("role", "status")
    this.matrixPlaceholderTarget.classList.add("hidden")
    this.#renderCompatibilitySummary(matrix.compatibility)

    const sizes = matrix.sizes || this.#extractSizes(matrix.colors)
    if (sizes.length === 0 || matrix.colors.length === 0) {
      const empty = document.createElement("p")
      empty.className = "text-sm text-black/50"
      empty.textContent = t("no_variants")
      this.matrixTarget.appendChild(empty)
      return
    }

    const variants = matrix.colors.flatMap(colour => colour.sizes || [])
    const prices = [ ...new Set(variants.map(variant => variant.customer_price_cents).filter(price => price != null)) ]
    const stockDates = [ ...new Set(variants.map(variant => variant.stock_updated_at).filter(Boolean)) ]
    const commonPrice = variants.length > 0 && variants.every(variant => variant.customer_price_cents != null) && prices.length === 1 ? prices[0] : null
    const commonStockDate = variants.length > 0 && variants.every(variant => variant.stock_updated_at) && stockDates.length === 1 ? stockDates[0] : null

    const heading = document.createElement("div")
    heading.className = "mb-3 flex flex-wrap items-start justify-between gap-2"

    const title = document.createElement("p")
    title.className = "text-sm font-semibold text-black"
    title.textContent = `${matrix.brand} ${matrix.product_name}`
    heading.appendChild(title)

    const metadata = document.createElement("div")
    metadata.className = "flex flex-wrap items-center justify-end gap-1.5"
    if (commonPrice != null) {
      const price = document.createElement("span")
      price.className = "rounded border border-blue-700 bg-erp-card px-2 py-1 text-xs font-semibold tabular-nums text-blue-700"
      price.dataset.textilePickerPriceSummary = ""
      price.textContent = t("client_price", { price: this.#formatMoney(commonPrice) })
      metadata.appendChild(price)
    }
    if (commonStockDate) {
      const stockDate = document.createElement("span")
      stockDate.className = "rounded border border-black/30 bg-erp-card px-2 py-1 text-xs text-black/60"
      stockDate.dataset.textilePickerStockSummary = ""
      stockDate.textContent = t("stock_as_of", { date: this.#formatDate(commonStockDate) })
      metadata.appendChild(stockDate)
    }
    if (metadata.childElementCount > 0) heading.appendChild(metadata)
    this.matrixTarget.appendChild(heading)

    const table = document.createElement("table")
    table.className = "w-full border-collapse text-sm"

    const headerRow = document.createElement("tr")
    const corner = document.createElement("th")
    corner.className = "sticky left-0 bg-erp-card z-10 px-2 py-2 text-left text-xs font-semibold text-black border-b border-black"
    corner.textContent = t("colourway")
    headerRow.appendChild(corner)

    sizes.forEach(size => {
      const th = document.createElement("th")
      th.className = "px-2 py-2 text-center text-xs font-semibold text-black border-b border-black whitespace-nowrap"
      th.textContent = matrix.size_labels?.[size] || size
      headerRow.appendChild(th)
    })

    const thead = document.createElement("thead")
    thead.appendChild(headerRow)
    table.appendChild(thead)

    const tbody = document.createElement("tbody")
    matrix.colors.forEach(colour => {
      const sizeMap = new Map()
      colour.sizes.forEach(size => {
        if (!sizeMap.has(size.size)) sizeMap.set(size.size, [])
        sizeMap.get(size.size).push(size)
      })
      const row = document.createElement("tr")
      row.className = "border-b border-black/10 hover:bg-black/[0.02]"
      row.dataset.compatibility = JSON.stringify(colour.compatibility || {})

      const labelCell = document.createElement("th")
      labelCell.scope = "row"
      labelCell.className = "sticky left-0 z-10 min-w-56 border-r border-black/10 bg-erp-card px-2 py-2"

      const labelWrap = document.createElement("div")
      labelWrap.className = "flex items-center gap-2"

      if (colour.image_url) {
        const img = document.createElement("img")
        img.addEventListener("error", () => img.replaceWith(this.#placeholder("h-10 w-10", "text-sm")), { once: true })
        img.src = colour.image_url
        img.alt = `${matrix.brand} ${matrix.product_name} — ${colour.name}`
        img.className = "h-10 w-10 flex-shrink-0 rounded border border-black bg-white object-contain"
        img.loading = "lazy"
        labelWrap.appendChild(img)
      }

      const labelDetails = document.createElement("div")
      labelDetails.className = "flex min-w-0 flex-col items-start"
      labelDetails.dataset.textilePickerColourDetails = ""

      const labelMain = document.createElement("div")
      labelMain.className = "flex min-w-0 items-center gap-2"
      labelMain.dataset.textilePickerColourMain = ""

      const colourHexes = Array.isArray(colour.hexes) && colour.hexes.length > 0 ? colour.hexes : [ colour.hex ].filter(Boolean)
      colourHexes.forEach(hex => {
        const swatch = document.createElement("span")
        swatch.className = "w-4 h-4 rounded-full border border-black flex-shrink-0"
        swatch.style.backgroundColor = hex
        labelMain.appendChild(swatch)
      })

      const name = document.createElement("span")
      name.className = "text-xs font-medium text-black whitespace-nowrap"
      name.textContent = colour.name
      labelMain.appendChild(name)
      labelDetails.appendChild(labelMain)

      const compatibility = document.createElement("div")
      compatibility.className = "mt-1"
      compatibility.dataset.textilePickerColourCompatibility = ""
      this.#appendColourCompatibility(compatibility, colour.compatibility)
      if (compatibility.childElementCount > 0) labelDetails.appendChild(compatibility)

      labelWrap.appendChild(labelDetails)
      labelCell.appendChild(labelWrap)
      row.appendChild(labelCell)

      sizes.forEach(size => {
        const cell = document.createElement("td")
        cell.className = "px-1 py-1.5 text-center"
        const variants = sizeMap.get(size) || []

        if (variants.length > 0) {
          const variantsWrap = document.createElement("div")
          variantsWrap.className = variants.length > 1 ? "flex flex-col gap-2" : ""
          variants.forEach(variant => variantsWrap.appendChild(this.#variantCell({
            matrix,
            colour,
            variant,
            showPrice: commonPrice == null,
            showStockDate: commonStockDate == null
          })))
          cell.appendChild(variantsWrap)
        } else {
          const dash = document.createElement("span")
          dash.className = "text-black/20"
          dash.textContent = "—"
          cell.appendChild(dash)
        }

        row.appendChild(cell)
      })

      tbody.appendChild(row)
    })
    table.appendChild(tbody)

    const scrollWrap = document.createElement("div")
    scrollWrap.className = "overflow-x-auto"
    scrollWrap.tabIndex = 0
    scrollWrap.setAttribute("role", "region")
    scrollWrap.setAttribute("aria-label", t("matrix_scroll_region"))
    scrollWrap.appendChild(table)
    this.matrixTarget.appendChild(scrollWrap)
  }

  #variantCell({ matrix, colour, variant, showPrice, showStockDate }) {
    const wrapper = document.createElement("div")
    wrapper.className = "flex flex-col items-center gap-0.5"

    const input = document.createElement("input")
    input.type = "number"
    input.min = "0"
    input.value = "0"
    input.className = "w-14 text-sm text-center border border-black/20 rounded bg-erp-input px-1 py-1 focus:outline-none focus:border-black tabular-nums"
    input.dataset.action = "input->erp--textile-picker#refreshFooter"
    input.dataset.variantId = variant.variant_id
    input.dataset.reference = variant.reference
    input.dataset.colorName = colour.name
    input.dataset.size = variant.size
    input.dataset.productId = matrix.product_id
    input.dataset.productName = `${matrix.brand} ${matrix.product_name}`
    input.dataset.supplier = matrix.supplier || ""
    input.dataset.productColorwayId = colour.product_colorway_id || ""
    input.dataset.pricingClass = colour.pricing_class || "color"
    input.dataset.canonicalSizeId = variant.canonical_size_id || ""
    input.dataset.canonicalSize = variant.canonical_size || variant.size
    input.dataset.sizeMappingStatus = variant.size_mapping_status || "unmapped"
    input.dataset.duplicateSize = variant.duplicate_size ? "true" : "false"
    input.dataset.supplierPriceCents = variant.supplier_price_cents ?? ""
    input.dataset.customerPriceCents = variant.customer_price_cents ?? ""
    input.dataset.imageUrl = colour.image_url || ""
    input.dataset.stockUpdatedAt = variant.stock_updated_at || ""
    const visibleSize = variant.canonical_size && variant.canonical_size !== variant.size
      ? `${variant.canonical_size} (${variant.size})`
      : (variant.canonical_size || variant.size)
    input.setAttribute("aria-label", t("quantity_for", { colour: colour.name, size: visibleSize }))
    const blockedReason = variant.size_mapping_status !== "mapped"
      ? t(variant.size_mapping_status === "ambiguous" ? "size_ambiguous" : "size_unmapped")
      : (variant.customer_price_cents == null ? t(this.customerIdValue > 0 ? "price_unavailable" : "select_customer_first") : null)
    if (blockedReason) {
      input.disabled = true
      input.setAttribute("aria-describedby", `variant-${colour.product_colorway_id || "none"}-${input.dataset.variantId}-availability`)
      input.classList.add("cursor-not-allowed", "bg-black/10", "text-black/40")
    }
    wrapper.appendChild(input)

    if (variant.duplicate_size && variant.reference) {
      const reference = document.createElement("span")
      reference.className = "max-w-24 text-[9px] leading-tight text-black/55"
      reference.textContent = variant.reference
      wrapper.appendChild(reference)
    }

    if (showPrice && variant.customer_price_cents != null) {
      const price = document.createElement("span")
      price.className = "text-[10px] font-semibold tabular-nums text-black"
      price.dataset.textilePickerCellPrice = ""
      price.textContent = t("client_price", { price: this.#formatMoney(variant.customer_price_cents) })
      wrapper.appendChild(price)
    }

    if (blockedReason) {
      const unavailable = document.createElement("span")
      unavailable.id = `variant-${colour.product_colorway_id || "none"}-${input.dataset.variantId}-availability`
      unavailable.className = "max-w-20 text-[10px] leading-tight text-red-700"
      unavailable.textContent = blockedReason
      wrapper.appendChild(unavailable)
    }

    const stock = variant.stock || 0
    const stockLabel = document.createElement("span")
    stockLabel.className = stock > 0 ? "text-[10px] tabular-nums text-black/50" : "text-[10px] tabular-nums text-red-500"
    stockLabel.textContent = t("stock", { count: stock })
    if (showStockDate && variant.stock_updated_at) {
      const stockDate = document.createElement("span")
      stockDate.className = "max-w-20 text-[9px] leading-tight text-black/45"
      stockDate.dataset.textilePickerCellStockDate = ""
      stockDate.textContent = t("stock_as_of", { date: this.#formatDate(variant.stock_updated_at) })
      wrapper.appendChild(stockDate)
    }
    wrapper.appendChild(stockLabel)

    return wrapper
  }

  #extractSizes(colors) {
    const sizes = new Set()
    colors.forEach(colour => colour.sizes.forEach(size => sizes.add(size.size)))
    return [ ...sizes ]
  }

  #singleColourway(colours) {
    if (colours.length === 0) return false

    return new Set(colours.map(colour => [
      colour.product_id,
      colour.product_colorway_id || colour.color_name
    ].join("|"))).size === 1
  }

  // One entry per colourway and customer price. Sizes sharing a price stay on one textile line;
  // a supplier size with a distinct cost becomes another line so no price is averaged silently.
  #catalogueColours() {
    const byColour = new Map()

    this.matrixTarget.querySelectorAll("input[type='number'][data-variant-id]").forEach(input => {
      const quantity = parseInt(input.value) || 0
      if (quantity <= 0) return

      const colourName = input.dataset.colorName
      const collisionReference = input.dataset.duplicateSize === "true" ? input.dataset.reference : ""
      const pricingKey = [ input.dataset.productColorwayId || colourName, input.dataset.customerPriceCents, input.dataset.supplierPriceCents, collisionReference ].join("|")
      if (!byColour.has(pricingKey)) {
        let compatibility = {}
        try {
          compatibility = JSON.parse(input.closest("tr")?.dataset.compatibility || "{}")
        } catch {
          compatibility = {}
        }
        byColour.set(pricingKey, {
          product_id: input.dataset.productId,
          product_colorway_id: input.dataset.productColorwayId || null,
          pricing_class: input.dataset.pricingClass,
          color_name: colourName,
          reference: input.dataset.reference,
          description: `${input.dataset.productName} — ${colourName}`,
          supplier: input.dataset.supplier,
          image_url: input.dataset.imageUrl || null,
          compatibility,
          supplier_price_cents: input.dataset.supplierPriceCents === ""
            ? null
            : parseInt(input.dataset.supplierPriceCents),
          unit_price_cents: input.dataset.customerPriceCents === ""
            ? null
            : parseInt(input.dataset.customerPriceCents),
          sizes: []
        })
      }

      byColour.get(pricingKey).sizes.push({
        variant_id: input.dataset.variantId,
        size: input.dataset.canonicalSize || input.dataset.size,
        supplier_size: input.dataset.size,
        canonical_size_id: input.dataset.canonicalSizeId || null,
        reference: input.dataset.reference,
        quantity
      })
    })

    return [ ...byColour.values() ]
  }

  #customSizes() {
    return this.sizeInputTargets
      .map(input => ({ size_label: input.dataset.sizeLabel, quantity: parseInt(input.value) || 0 }))
      .filter(size => size.quantity > 0)
  }

  #garmentLabel() {
    const button = this.garmentButtonTargets.find(b => b.dataset.garmentType === this._garmentType)
    return button?.textContent.trim() || ""
  }

  #step(button, delta) {
    const input = button.parentElement.querySelector("input[type='number']")
    if (!input) return

    input.value = Math.max(0, (parseInt(input.value) || 0) + delta)
    this.refreshFooter()
  }

  #placeholder(sizeClasses, iconClasses) {
    const wrapper = document.createElement("div")
    wrapper.className = `${sizeClasses} rounded border border-black bg-black/5 flex items-center justify-center`
    wrapper.dataset.erpImagePlaceholder = ""
    const icon = document.createElement("i")
    icon.className = `lni lni-gallery ${iconClasses} text-black/20`
    wrapper.appendChild(icon)
    return wrapper
  }

  #renderCompatibilitySummary(summary = {}) {
    if (!this.hasCompatibilityDetailsTarget || !this.hasCompatibilitySummaryTarget) return

    this.compatibilitySummaryTarget.replaceChildren()
    const counts = summary?.counts || summary || {}
    const rules = Array.isArray(summary?.rules) ? summary.rules : []
    const entries = Object.entries(counts).filter(([, count]) => Number(count) > 0)
    this.compatibilityDetailsTarget.classList.toggle("hidden", entries.length === 0 && rules.length === 0)

    const badges = document.createElement("div")
    badges.className = "flex flex-wrap gap-2"

    entries.forEach(([status, count]) => {
      const badge = document.createElement("span")
      badge.className = "rounded border border-black bg-erp-card px-2.5 py-1.5 text-xs text-black"
      badge.textContent = t(`compatibility.${status}`, { count })
      badges.appendChild(badge)
    })
    if (entries.length > 0) this.compatibilitySummaryTarget.appendChild(badges)

    rules.forEach(rule => {
      const row = document.createElement("div")
      row.className = "rounded border border-black/20 bg-erp-card px-3 py-2 text-xs text-black"
      const title = document.createElement("p")
      title.className = "font-semibold"
      title.textContent = `${rule.technique} · ${rule.zone} — ${t(`compatibility_status.${rule.status}`)}`
      const metadata = [
        rule.max_width_mm || rule.max_height_mm
          ? t("max_dimensions", { width: rule.max_width_mm || "—", height: rule.max_height_mm || "—" })
          : null,
        rule.inherited ? t("inherited_rule") : null,
        rule.operator_note || null
      ].filter(Boolean)
      row.appendChild(title)
      if (metadata.length > 0) {
        const detail = document.createElement("p")
        detail.className = "mt-1 text-black/60"
        detail.textContent = metadata.join(" · ")
        row.appendChild(detail)
      }
      this.compatibilitySummaryTarget.appendChild(row)
    })
  }

  #appendColourCompatibility(container, summary = {}) {
    const counts = summary?.counts || {}
    const statuses = ["incompatible", "approval_required", "unspecified", "allowed"]
      .filter(status => Number(counts[status]) > 0)
    if (statuses.length === 0) return

    const info = document.createElement("span")
    info.className = "flex min-w-0 flex-wrap items-center gap-1"
    statuses.forEach(status => {
      const badge = document.createElement("span")
      const colors = {
        incompatible: "border-red-700 bg-red-50 text-red-800",
        approval_required: "border-amber-700 bg-amber-50 text-amber-800",
        unspecified: "border-black/30 bg-erp-card text-black/65",
        allowed: "border-green-700 bg-green-50 text-green-800"
      }[status]
      badge.className = `rounded border px-1.5 py-0.5 text-[9px] font-medium ${colors}`
      badge.textContent = t(`compatibility.${status}`, { count: counts[status] })
      info.appendChild(badge)
    })
    container.appendChild(info)
  }

  #formatMoney(cents) {
    return new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      .format(Number(cents) / 100)
  }

  #formatDate(value) {
    return new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value))
  }
}
